// APIDocs-v2.js
import { 
  Box, Grid, GridItem, Text, VStack, HStack, Link, Divider, Heading, 
  Code, Container, Button, Spinner, Badge, Tabs, TabList, TabPanels, 
  Tab, TabPanel, useColorModeValue, UnorderedList, ListItem, Table,
  Thead, Tbody, Tr, Th, Td, Accordion, AccordionItem, AccordionButton,
  AccordionPanel, AccordionIcon, OrderedList
} from "@chakra-ui/react";
import { ExternalLinkIcon, InfoIcon } from '@chakra-ui/icons';
import HeaderMenus from './HeaderMenus';
import React, { useState, useContext } from 'react';
import { AppContext } from '../components/AppContext';
import { callApi } from '../callApi';

function APIDocsV2() {
  const { accessToken } = useContext(AppContext);
  const [credentials, setCredentials] = useState(null);
  const [loadingCredentials, setLoadingCredentials] = useState(false);
  const [version, setVersion] = useState([]);
  const [loadingVersion, setLoadingVersion] = useState(false);
  
  const bgColor = useColorModeValue("white", "gray.800");
  const borderColor = useColorModeValue("gray.200", "gray.700");
  const codeBg = useColorModeValue("gray.50", "gray.700");
  const accentColor = useColorModeValue("blue.500", "blue.300");

  const handleGetVersionClick = async () => {
    setLoadingVersion(true);
    try {
      const response = await callApi('/version', accessToken, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
        },
      });
      const data = await response.json();
      setVersion(data.version);
    } catch (error) {
      console.error("Error fetching version: ", error);
    }
    setLoadingVersion(false);
  };

  const handleGetCredentialsClick = async () => {
    setLoadingCredentials(true);
    try {
      const response = await callApi('/getcredentials', accessToken, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
        },
      });
      const data = await response.json();
      setCredentials(data);
    } catch (error) {
      console.error("Error fetching credentials: ", error);
    }
    setLoadingCredentials(false);
  };

  const EndpointSection = ({ id, title, method, endpoint, description, headers, pathParams, queryParams, requestBody, responses, example }) => (
    <Box id={id} mb={8} p={4} borderWidth="1px" borderRadius="lg" borderColor={borderColor}>
      <HStack mb={4}>
        <Badge colorScheme={
          method === 'GET' ? 'green' : 
          method === 'POST' ? 'blue' : 
          method === 'PUT' ? 'orange' : 
          method === 'DELETE' ? 'red' : 'gray'
        } fontSize="md" px={2} py={1}>{method}</Badge>
        <Heading as="h3" size="md">{title}</Heading>
      </HStack>
      
      <Text mb={4}>{description}</Text>
      
      <Box mb={4}>
        <Heading as="h4" size="sm" mb={2}>Endpoint</Heading>
        <Code p={2} borderRadius="md" bg={codeBg} children={endpoint} />
      </Box>
      
      {headers && (
        <Box mb={4}>
          <Heading as="h4" size="sm" mb={2}>Headers</Heading>
          <Code p={2} borderRadius="md" bg={codeBg} display="block" whiteSpace="pre">
            {headers}
          </Code>
        </Box>
      )}
      
      {pathParams && (
        <Box mb={4}>
          <Heading as="h4" size="sm" mb={2}>Path Parameters</Heading>
          <Table size="sm" variant="simple">
            <Thead>
              <Tr>
                <Th>Parameter</Th>
                <Th>Description</Th>
                <Th>Required</Th>
              </Tr>
            </Thead>
            <Tbody>
              {pathParams.map((param, index) => (
                <Tr key={index}>
                  <Td><Code>{param.name}</Code></Td>
                  <Td>{param.description}</Td>
                  <Td>{param.required ? "Yes" : "No"}</Td>
                </Tr>
              ))}
            </Tbody>
          </Table>
        </Box>
      )}
      
      {queryParams && (
        <Box mb={4}>
          <Heading as="h4" size="sm" mb={2}>Query Parameters</Heading>
          <Table size="sm" variant="simple">
            <Thead>
              <Tr>
                <Th>Parameter</Th>
                <Th>Description</Th>
                <Th>Required</Th>
              </Tr>
            </Thead>
            <Tbody>
              {queryParams.map((param, index) => (
                <Tr key={index}>
                  <Td><Code>{param.name}</Code></Td>
                  <Td>{param.description}</Td>
                  <Td>{param.required ? "Yes" : "No"}</Td>
                </Tr>
              ))}
            </Tbody>
          </Table>
        </Box>
      )}
      
      {requestBody && (
        <Box mb={4}>
          <Heading as="h4" size="sm" mb={2}>Request Body</Heading>
          <Code p={2} borderRadius="md" bg={codeBg} display="block" whiteSpace="pre">
            {requestBody}
          </Code>
        </Box>
      )}
      
      {responses && (
        <Box mb={4}>
          <Heading as="h4" size="sm" mb={2}>Responses</Heading>
          <Accordion allowMultiple>
            {responses.map((response, index) => (
              <AccordionItem key={index}>
                <h2>
                  <AccordionButton>
                    <Box flex="1" textAlign="left">
                      <Badge colorScheme={
                        response.status >= 200 && response.status < 300 ? 'green' :
                        response.status >= 400 && response.status < 500 ? 'yellow' :
                        response.status >= 500 ? 'red' : 'gray'
                      }>{response.status}</Badge>
                      {' '}{response.description}
                    </Box>
                    <AccordionIcon />
                  </AccordionButton>
                </h2>
                <AccordionPanel pb={4}>
                  <Code p={2} borderRadius="md" bg={codeBg} display="block" whiteSpace="pre">
                    {response.example}
                  </Code>
                </AccordionPanel>
              </AccordionItem>
            ))}
          </Accordion>
        </Box>
      )}
      
      {example && (
        <Box mb={4}>
          <Heading as="h4" size="sm" mb={2}>Example Request</Heading>
          <Code p={2} borderRadius="md" bg={codeBg} display="block" whiteSpace="pre">
            {example}
          </Code>
        </Box>
      )}
    </Box>
  );

  return (
    <Box>
      <Grid
        templateAreas={`"header header"    
                        "nav main"
                        "footer footer"`}
        gridTemplateRows={'60px 1fr 60px'}
        gridTemplateColumns={'300px 1fr'}
        h='100vh'
        gap='2'
        color='blackAlpha.800'
        fontWeight='bold'
      >
        {/* Header with menu */}
        <HeaderMenus />

        {/* Navigation sidebar */}
        <GridItem area="nav" bg={bgColor} p={4} overflowY="auto" borderRight="1px" borderColor={borderColor}>
          <VStack spacing={4} align="start" width="100%">
            <Heading as="h3" size="md" mb={2}>Revedy API v2</Heading>
            
            <VStack align="start" spacing={2} width="100%">
              <Link href="#overview" fontSize="md" fontWeight="semibold">Overview</Link>
              <Link href="#authentication" fontSize="md" fontWeight="semibold">Authentication</Link>
              
              <Heading as="h4" size="sm" mt={2}>Encounters</Heading>
              <Link href="#list-encounters" fontSize="sm" pl={4}>List Encounters</Link>
              <Link href="#search-encounters" fontSize="sm" pl={4}>Search Encounters</Link>
              <Link href="#get-encounter" fontSize="sm" pl={4}>Get Encounter</Link>
              <Link href="#generate-note-pdf" fontSize="sm" pl={4}>Generate Note PDF</Link>
              
              <Heading as="h4" size="sm" mt={2}>Encounter Analysis</Heading>
              <Link href="#encounter-analysis" fontSize="sm" pl={4}>Analyze Encounter</Link>
              <Link href="/ea" isExternal fontSize="sm" pl={4}>
                Try it <ExternalLinkIcon mx="2px" />
              </Link>
              
              <Heading as="h4" size="sm" mt={2}>Notes Management</Heading>
              <Link href="#get-latest-note" fontSize="sm" pl={4}>Get Latest Note</Link>
              <Link href="#edit-note" fontSize="sm" pl={4}>Edit Note</Link>
              <Link href="#save-note" fontSize="sm" pl={4}>Save Note</Link>
              <Link href="#get-recent-note" fontSize="sm" pl={4}>Get Recent Note</Link>
              
              <Heading as="h4" size="sm" mt={2}>Patient Content</Heading>
              <Link href="#generate-patient-content" fontSize="sm" pl={4}>Generate Patient Content</Link>
              <Link href="#get-recent-patient-content" fontSize="sm" pl={4}>Get Recent Patient Content</Link>
              <Link href="#get-all-patient-content" fontSize="sm" pl={4}>Get All Patient Content</Link>
              
              <Heading as="h4" size="sm" mt={2}>Case Management</Heading>
              <Link href="#create-external-case" fontSize="sm" pl={4}>Create External Case</Link>
              <Link href="#list-external-cases" fontSize="sm" pl={4}>List External Cases</Link>
              <Link href="#get-case-details" fontSize="sm" pl={4}>Get Case Details</Link>

              <Heading as="h4" size="sm" mt={2}>Content Management</Heading>
              <Link href="#simplified-upload-file" fontSize="sm" pl={4} color={accentColor} fontWeight="bold">Simplified File Upload</Link>
              <Link href="#upload-content" fontSize="sm" pl={4}>Upload Content (Legacy)</Link>
              <Link href="#upload-file" fontSize="sm" pl={4}>Upload File (Legacy)</Link>
              <Link href="#download-case-file" fontSize="sm" pl={4}>Download File</Link>

              <Heading as="h4" size="sm" mt={2}>Coding Services</Heading>
              <Link href="#cpt-coding" fontSize="sm" pl={4}>CPT Coding</Link>
              <Link href="#icd10-coding" fontSize="sm" pl={4}>ICD-10 Coding</Link>
              <Link href="#generate-case-overview" fontSize="sm" pl={4}>Generate Case Overview</Link>

              <Heading as="h4" size="sm" mt={2}>Medical Necessity</Heading>
              <Link href="#medical-necessity-analysis" fontSize="sm" pl={4}>Analyze Medical Necessity</Link>

              <Heading as="h4" size="sm" mt={2}>Other</Heading>
              <Link href="#error-codes" fontSize="sm" pl={4}>Error Codes</Link>
              <Link href="#rate-limits" fontSize="sm" pl={4}>Rate Limits</Link>
              <Link href="#version-info" fontSize="sm" pl={4}>Version Info</Link>
            </VStack>
          </VStack>
        </GridItem>

        {/* Main content area */}
        <GridItem area="main" bg={bgColor} p={6} overflowY="auto">
          <Box maxW="1000px" mx="auto">
            {/* Overview Section */}
            <Box id="overview" mb={10}>
              <Heading as="h1" size="xl" mb={4}>Revedy API Documentation</Heading>
              <Text fontSize="lg" mb={4}>
                The Revedy API provides a secure way for partners to programmatically access our services. 
                This documentation covers the v2 API endpoints and non-versioned endpoints that are currently supported.
              </Text>
              <Box p={4} borderLeft="4px" borderColor={accentColor} bg={codeBg} borderRadius="md">
                <Text fontWeight="bold">Base URL</Text>
                <Code>https://api.revedy.io</Code>
              </Box>
            </Box>

            {/* Authentication Section */}
            <Box id="authentication" mb={10}>
              <Heading as="h2" size="lg" mb={4}>Authentication</Heading>
              <Text mb={4}>
                To access the API, clients must first obtain an access token by providing their unique client credentials. 
                This token is then used in the Authorization header as a Bearer token to authenticate API requests.
              </Text>
              <Text mb={4}>
                All interactions with the API should be conducted over HTTPS to ensure the confidentiality and integrity of the data transmitted.
              </Text>
              
              <EndpointSection
                id="auth-token"
                title="Obtain Access Token"
                method="POST"
                endpoint="/oauth/token"
                description="Obtain an OAuth 2.0 access token for API authentication."
                headers="Content-Type: application/json"
                requestBody={`{
  "client_id": "YOUR_CLIENT_ID",
  "client_secret": "YOUR_CLIENT_SECRET",
  "audience": "https://revedy.io/demo",
  "grant_type": "client_credentials"
}`}
                responses={[
                  {
                    status: 200,
                    description: "Successful response",
                    example: `{
  "access_token": "eyJhbGciOiJSUzI1NiIsInR5cCI6IkpXVCIsImtpZCI6Ik...",
  "token_type": "Bearer",
  "expires_in": 86400,
  "scope": ""
}`
                  },
                  {
                    status: 401,
                    description: "Unauthorized - Invalid credentials",
                    example: `{
  "error": "access_denied",
  "error_description": "Unauthorized"
}`
                  }
                ]}
                example={`curl --request POST \\
  --url 'https://api.revedy.io/oauth/token' \\
  --header 'content-type: application/json' \\
  --data '{
    "client_id": "YOUR_CLIENT_ID",
    "client_secret": "YOUR_CLIENT_SECRET",
    "audience": "https://revedy.io/demo",
    "grant_type": "client_credentials"
  }'`}
              />
              
              <Box id="credentials" mt={6}>
                <Heading as="h3" size="md" mb={4}>Client Credentials</Heading>
                <Text mb={4}>
                  You can retrieve your client credentials using the button below:
                </Text>
                <Button 
                  onClick={handleGetCredentialsClick} 
                  isLoading={loadingCredentials}
                  colorScheme="blue"
                  mb={4}
                >
                  Get Credentials
                </Button>
                {credentials && (
                  <Box p={4} borderWidth="1px" borderRadius="md" bg={codeBg}>
                    <Text><strong>Client ID:</strong> {credentials.clientId}</Text>
                    <Text><strong>Client Secret:</strong> {credentials.clientSecret}</Text>
                  </Box>
                )}
              </Box>
            </Box>

            {/* Encounters Section */}
            <Box mb={10}>
              <Heading as="h2" size="lg" mb={6}>Encounters</Heading>
              
              <EndpointSection
                id="list-encounters"
                title="List Encounters"
                method="GET"
                endpoint="/v2/encounters"
                description="Retrieves a list of all encounters associated with the authenticated user."
                headers="Authorization: Bearer YOUR_ACCESS_TOKEN"
                responses={[
                  {
                    status: 200,
                    description: "Successful response",
                    example: `{
  "encounters": [
    {
      "external_encounter_id": "ENC123", 
      "id": "b1a4ce7f-a9e9-4f13-8df0-66a221d57b57", 
      "create_date": "2023-01-01T12:00:00Z"
    },
    {
      "external_encounter_id": "ENC456", 
      "id": "775ab4e4-f611-4a81-b077-7658f066c793", 
      "create_date": "2023-01-02T12:00:00Z"
    }
  ]
}`
                  },
                  {
                    status: 401,
                    description: "Unauthorized - Invalid or missing token",
                    example: `{
  "error": "Unauthorized access"
}`
                  }
                ]}
                example={`curl --request GET \\
  --url 'https://api.revedy.io/v2/encounters' \\
  --header 'Authorization: Bearer YOUR_ACCESS_TOKEN'`}
              />
              
              <EndpointSection
                id="search-encounters"
                title="Search Encounters"
                method="POST"
                endpoint="/v2/encounters/search"
                description="Search for encounters based on external provider and patient IDs."
                headers="Authorization: Bearer YOUR_ACCESS_TOKEN\nContent-Type: application/json"
                requestBody={`{
  "external_provider_id": "PROVIDER123",
  "external_patient_id": "PATIENT456"
}`}
                responses={[
                  {
                    status: 200,
                    description: "Successful response",
                    example: `{
  "encounters": [
    {
      "external_encounter_id": "ENC123", 
      "id": "b1a4ce7f-a9e9-4f13-8df0-66a221d57b57", 
      "create_date": "2023-01-01T12:00:00Z"
    },
    {
      "external_encounter_id": "ENC456", 
      "id": "775ab4e4-f611-4a81-b077-7658f066c793", 
      "create_date": "2023-01-02T12:00:00Z"
    }
  ]
}`
                  },
                  {
                    status: 401,
                    description: "Unauthorized - Invalid or missing token",
                    example: `{
  "error": "Unauthorized access"
}`
                  }
                ]}
                example={`curl --request POST \\
  --url 'https://api.revedy.io/v2/encounters/search' \\
  --header 'Authorization: Bearer YOUR_ACCESS_TOKEN' \\
  --header 'Content-Type: application/json' \\
  --data '{
    "external_provider_id": "PROVIDER123",
    "external_patient_id": "PATIENT456"
  }'`}
              />
              
              <EndpointSection
                id="get-encounter"
                title="Get Encounter"
                method="GET"
                endpoint="/v2/encounters/{encounter_id}"
                description="Retrieves detailed information about a specific encounter."
                headers="Authorization: Bearer YOUR_ACCESS_TOKEN"
                pathParams={[
                  {
                    name: "encounter_id",
                    description: "The ID of the encounter to retrieve (can be internal or external ID)",
                    required: true
                  }
                ]}
                responses={[
                  {
                    status: 200,
                    description: "Successful response",
                    example: `{
  "external_encounter_id": "ENC123",
  "revedy_encounter_id": "b1a4ce7f-a9e9-4f13-8df0-66a221d57b57",
  "external_case_id": "350ef940-d86d-412d-940c-78b522ad6e51",
  "format": "v2",
  "note": "New Patient Assessment\\n\\nSUBJECTIVE:\\nChief Complaint: Neck and left shoulder pain for a few weeks\\n\\nHistory of Present Illness: 55-year-old male presents with neck and left shoulder pain...",
  "note_id": 3145,
  "structured_transcript": "Patient: Okay I'm doing all right I've been having this pain in my neck...",
  "transcribed_text": "okay I'm doing all right I've been having this pain in my neck...",
  "results": {
    "codes": {
      "cpt_codes": {
        "case_description": "Encounter ENC123",
        "case_overview": "E/M Code: 99204\\nMDM Complexity: Moderate\\nPatient Type: New\\n\\nProblems Addressed:\\n- Suspected cervical radiculopathy: Undiagnosed new problem with uncertain prognosis",
        "code_type": "cpt",
        "coder_notes": "",
        "cpt_codes": {
          "99204": {
            "billable": true,
            "cpt_code": "99204",
            "decision": "E/M code 99204 selected",
            "detail": "Overall MDM complexity: moderate",
            "justification": "E/M code 99204 selected based on:\\n- Problem complexity: moderate\\n- Data complexity: low\\n- Risk level: moderate\\n- Patient type: new",
            "long_description": "Office or other outpatient visit for the evaluation and management of a new patient...",
            "modifiers": "",
            "units": 1
          }
        }
      },
      "icd10_codes": {
        "M25.512": {
          "clean_code": "M25512",
          "code_type": "diagnosis",
          "condition": "Differential diagnosis of rotator cuff pathology with referred pain",
          "confidence": 85,
          "description": "Pain in left shoulder",
          "evidence": "Listed in Assessment as \\"Differential diagnosis includes rotator cuff pathology with referred pain\\"...",
          "icd10_code": "M25.512",
          "is_primary": false,
          "justification": "The documentation indicates left shoulder pain with a differential diagnosis that includes rotator cuff pathology...",
          "status": "current"
        },
        "M54.12": {
          "clean_code": "M5412",
          "code_type": "diagnosis",
          "condition": "Suspected cervical radiculopathy",
          "confidence": 85,
          "description": "Radiculopathy, cervical region",
          "evidence": "Listed in Assessment as \\"Suspected cervical radiculopathy, likely at C5-6 or C6 level\\"...",
          "icd10_code": "M54.12",
          "is_primary": true,
          "justification": "The documentation describes a suspected cervical radiculopathy with specific clinical findings supporting this diagnosis...",
          "status": "current"
        }
      }
    },
    "note": "New Patient Assessment\\n\\nSUBJECTIVE:\\nChief Complaint: Neck and left shoulder pain for a few weeks\\n\\nHistory of Present Illness: 55-year-old male presents with neck and left shoulder pain...",
    "note_id": 3145,
    "structured_transcript": "Patient: Okay I'm doing all right I've been having this pain in my neck...",
    "transcribed_text": "okay I'm doing all right I've been having this pain in my neck..."
  }
}`
                  },
                  {
                    status: 404,
                    description: "Not Found - Encounter not found or access denied",
                    example: `{
  "error": "Encounter not found or access denied"
}`
                  },
                  {
                    status: 500,
                    description: "Internal Server Error",
                    example: `{
  "error": "internal error"
}`
                  }
                ]}
                example={`curl --request GET \\
  --url 'https://api.revedy.io/v2/encounters/ENC123' \\
  --header 'Authorization: Bearer YOUR_ACCESS_TOKEN'`}
              />
              
              <EndpointSection
                id="generate-note-pdf"
                title="Generate Note PDF"
                method="GET"
                endpoint="/generate_note_pdf/{external_encounter_id}/{template_id}"
                description="Generates a PDF of the clinical note associated with a specific encounter. You can optionally specify a template_id to use for the PDF generation. If no template_id is provided, the system will use the template associated with the note, if one exists."
                headers="Authorization: Bearer YOUR_ACCESS_TOKEN"
                pathParams={[
                  {
                    name: "external_encounter_id",
                    description: "The external identifier for the encounter.",
                    required: true
                  },
                  {
                    name: "template_id",
                    description: "The ID of the template to use for the PDF. If omitted, the template linked to the encounter's note will be used.",
                    required: false
                  }
                ]}
                responses={[
                  {
                    status: 200,
                    description: "Successful response - Returns a PDF file.",
                    example: "Binary PDF data"
                  },
                  {
                    status: 404,
                    description: "Not Found - Encounter not found, access denied, or template not found.",
                    example: `{
  "error": "Resource not found or access denied"
}`
                  }
                ]}
                example={`# Generate PDF using the default template for the encounter
curl --request GET \\
  --url 'https://api.revedy.io/generate_note_pdf/ENC123' \\
  --header 'Authorization: Bearer YOUR_ACCESS_TOKEN' \\
  -O -J

# Generate PDF using a specific template
curl --request GET \\
  --url 'https://api.revedy.io/generate_note_pdf/ENC123/101' \\
  --header 'Authorization: Bearer YOUR_ACCESS_TOKEN' \\
  -O -J`}
              />
            </Box>

            {/* Encounter Analysis Section */}
            <Box mb={10}>
              <Heading as="h2" size="lg" mb={6}>Encounter Analysis</Heading>
              
              <EndpointSection
                id="encounter-analysis"
                title="Analyze Encounter"
                method="POST"
                endpoint="/v2/encounter-analysis"
                description="Process an encounter for analysis, including audio transcription, note generation, and medical coding."
                headers="Authorization: Bearer YOUR_ACCESS_TOKEN\nContent-Type: multipart/form-data"
                requestBody={`encounter_id: Unique identifier for the encounter in your system.
external_patient_id: External identifier for the patient.
external_provider_id: External identifier for the provider.
fhir_patient_reference: FHIR reference for the patient.
  fhir_practitioner_reference: FHIR reference for the practitioner.
  fhir_location_reference: FHIR reference for the location.
  analysis_type: The type(s) of analysis to be performed (e.g., "coding").
  note_template_id: Identifier for the note template to be used.
  note_guidelines_id: Identifier for the note guidelines to be followed.
  encounter_type_id: Identifier for the encounter type.
  audio: The audio file to be processed.
  external_note: The external note to be processed. 
  transcript: The transcript to be processed.
  additional_data: JSON array containing additional data points.
  format: Response format (default: "v2")`}
                  responses={[
                    {
                      status: 200,
                      description: "Successful response",
                      example: `{
    "external_encounter_id": "3373",
    "revedy_encounter_id": "c9143b8d-602c-414f-b49c-c3127e17c65f",
    "external_case_id": "350ef940-d86d-412d-940c-78b522ad6e51",
    "format": "v2",
    "note": "New Patient Assessment\\nSUBJECTIVE:\\nChief Complaint: Neck and left shoulder pain for a few weeks\\n\\nHistory of Present Illness: 55-year-old male presents with neck and left shoulder pain...",
  "note_id": 3145,
  "results": {
    "codes": {
      "cpt_codes": {
        "case_description": "Encounter 3373",
        "case_overview": "E/M Code: 99204\\nMDM Complexity: Moderate\\nPatient Type: New",
        "code_type": "cpt",
        "cpt_codes": {
          "99204": {
            "billable": true,
            "cpt_code": "99204",
            "decision": "E/M code 99204 selected",
            "detail": "Overall MDM complexity: moderate",
            "justification": "E/M code 99204 selected based on:\\n- Problem complexity: moderate\\n- Data complexity: low\\n- Risk level: moderate\\n- Patient type: new",
            "long_description": "Office or other outpatient visit for the evaluation and management of a new patient...",
            "units": 1
          }
        }
      },
      "icd10_codes": {
        "M25.512": {
          "clean_code": "M25512",
          "code_type": "diagnosis",
          "condition": "Differential diagnosis of rotator cuff pathology with referred pain",
          "confidence": 85,
          "description": "Pain in left shoulder",
          "icd10_code": "M25.512",
          "is_primary": false,
          "justification": "The documentation indicates left shoulder pain with a differential diagnosis..."
        },
        "M54.12": {
          "clean_code": "M5412",
          "code_type": "diagnosis",
          "condition": "Suspected cervical radiculopathy",
          "confidence": 85,
          "description": "Radiculopathy, cervical region",
          "icd10_code": "M54.12",
          "is_primary": true,
          "justification": "The documentation describes a suspected cervical radiculopathy..."
        }
      }
    },
    "structured_transcript": "Patient: Okay I'm doing all right I've been having this pain in my neck...",
    "transcribed_text": "okay I'm doing all right I've been having this pain in my neck..."
  }
}`
                  },
                  {
                    status: 400,
                    description: "Bad Request - No source data provided",
                    example: `{
  "error": "No source data provided"
}`
                  },
                  {
                    status: 500,
                    description: "Internal Server Error",
                    example: `{
  "error": "An internal server error occurred"
}`
                  }
                ]}
                example={`curl --request POST \\
  --url 'https://api.revedy.io/v2/encounter-analysis' \\
  --header 'Authorization: Bearer YOUR_ACCESS_TOKEN' \\
  --header 'Content-Type: multipart/form-data' \\
  --form 'encounter_id=ENC123' \\
  --form 'external_patient_id=PAT456' \\
  --form 'external_provider_id=DOC789' \\
  --form 'analysis_type=coding' \\
  --form 'audio=@patient_encounter.mp3' \\
  --form 'additional_data=[
    {
      "data_key": "chief_complaint",
      "data_value": "Patient complains of severe headache and dizziness",
      "data_type": "string"
    },
    {
      "data_key": "patient_name",
      "data_value": "John Doe",
      "data_type": "string"
    }
  ]'`}
              />
              
              <Box p={4} mt={4} borderWidth="1px" borderRadius="lg" borderColor={borderColor}>
                <HStack mb={2}>
                  <InfoIcon color={accentColor} />
                  <Heading as="h4" size="sm">Additional Data Format</Heading>
                </HStack>
                <Text mb={2}>
                  The <Code>additional_data</Code> parameter allows you to store additional patient and encounter data of any type.
                  This data will be returned with any Encounter request.
                </Text>
                <Code p={2} borderRadius="md" bg={codeBg} display="block" whiteSpace="pre">
{`{
  "data_key": "string", // Required - Unique identifier for the data point
  "data_value": "string", // Required - Value of the data point
  "data_type": "string", // Required - Data type of the value (e.g., string, integer, date)
  "fhir_resource_type": "string", // Optional - FHIR resource type associated with the data point
  "fhir_resource_id": "string", // Optional - Unique identifier for the FHIR resource
  "fhir_path": "string", // Optional - FHIR path to locate the relevant information within the resource
  "fhir_display": "string", // Optional - Human-readable display text for the data point
  "fhir_extension_url": "string", // Optional - URL for a FHIR extension providing additional information
  "fhir_extension_value": "string" // Optional - Value for the FHIR extension
  "fhir_version": "string" // Optional - FHIR version (e.g., 4.0.1)
}`}
                </Code>
              </Box>
              
              <Box p={4} mt={4} borderWidth="1px" borderRadius="lg" borderColor={borderColor}>
                <HStack mb={2}>
                  <InfoIcon color={accentColor} />
                  <Heading as="h4" size="sm">Multiple Audio Files or Transcripts</Heading>
                </HStack>
                <Text mb={2}>
                  The endpoint supports processing multiple audio files or multiple transcripts. 
                  If you send multiple files, they will be concatenated in the order received.
                </Text>
                <Code p={2} borderRadius="md" bg={codeBg} display="block" whiteSpace="pre">
{`# Multiple audio files example
curl --request POST \\
  --url 'https://api.revedy.io/v2/encounter-analysis' \\
  --header 'Authorization: Bearer YOUR_ACCESS_TOKEN' \\
  --header 'Content-Type: multipart/form-data' \\
  --form 'audio=@file1.mp3' \\
  --form 'audio=@file2.mp3' \\
  --form 'audio=@file3.mp3'

# Multiple transcripts example
curl --request POST \\
  --url 'https://api.revedy.io/v2/encounter-analysis' \\
  --header 'Authorization: Bearer YOUR_ACCESS_TOKEN' \\
  --header 'Content-Type: multipart/form-data' \\
  --form 'transcript=First part of the transcript.' \\
  --form 'transcript=Second part of the transcript.' \\
  --form 'transcript=Third part of the transcript.'`}
                </Code>
              </Box>
            </Box>

            {/* Notes Management Section */}
            <Box mb={10}>
              <Heading as="h2" size="lg" mb={6}>Notes Management</Heading>
              
              <EndpointSection
                id="get-latest-note"
                title="Get Latest Encounter Note"
                method="GET"
                endpoint="/v2/encounters/{encounter_id}/notes/latest"
                description="Retrieves the most recent note for a specific encounter."
                headers="Authorization: Bearer YOUR_ACCESS_TOKEN"
                pathParams={[
                  {
                    name: "encounter_id",
                    description: "The internal or external identifier for the encounter",
                    required: true
                  }
                ]}
                responses={[
                  {
                    status: 200,
                    description: "Successful response",
                    example: `{
  "note": {
    "id": 1234,
    "note_text": "Patient presented with symptoms of...",
    "description": "Follow-up visit",
    "created_at": "2023-06-01T14:30:00Z",
    "prompt": "Summarize the patient encounter",
    "model_parameters": "temperature=0.7",
    "icd10_codes": ["M54.5", "I10"]
  },
  "encounter_id": "5678",
  "external_encounter_id": "ENC123",
  "external_case_id": "9012"
}`
                  },
                  {
                    status: 404,
                    description: "Not Found - Encounter not found or no notes available",
                    example: `{
  "error": "Encounter not found or no notes available"
}`
                  },
                  {
                    status: 500,
                    description: "Internal Server Error",
                    example: `{
  "error": "Internal server error"
}`
                  }
                ]}
                example={`curl --request GET \\
  --url 'https://api.revedy.io/v2/encounters/ENC123/notes/latest' \\
  --header 'Authorization: Bearer YOUR_ACCESS_TOKEN'`}
              />
              
              <EndpointSection
                id="edit-note"
                title="Edit Encounter Note"
                method="POST"
                endpoint="/v2/encounters/{encounter_id}/notes/edit"
                description="Edits an existing note for a specific encounter and optionally recodes it."
                headers="Authorization: Bearer YOUR_ACCESS_TOKEN\nContent-Type: application/json"
                pathParams={[
                  {
                    name: "encounter_id",
                    description: "The internal or external identifier for the encounter",
                    required: true
                  }
                ]}
                requestBody={`{
  "note_text": "Updated note content with patient information...",
  "description": "Edited note",
  "prompt": "Optional prompt used for generation",
  "model_parameters": "Optional model parameters",
  "icd10_codes": ["Optional", "ICD10", "Codes"]
}`}
                responses={[
                  {
                    status: 200,
                    description: "OK - Note edited and analysis completed successfully",
                    example: `{
  "message": "Note edited and analysis completed successfully",
  "external_case_id": "9012",
  "encounter_id": "5678",
  "external_encounter_id": "ENC123",
  "results": {
    "codes": {
      "cpt_codes": {
        "99213": {
          "code": "99213",
          "description": "Office or other outpatient visit...",
          "justification": "Based on the documented history..."
        }
      },
      "icd10_codes": {
        "M54.5": {
          "code": "M54.5",
          "description": "Low back pain",
          "justification": "Patient reports chronic lower back pain..."
        },
        "I10": {
          "code": "I10",
          "description": "Essential (primary) hypertension",
          "justification": "Patient has documented history of hypertension..."
        }
      }
    }
  }
}`
                  },
                  {
                    status: 400,
                    description: "Bad Request - No data provided or note text is missing",
                    example: `{
  "error": "No data provided"
}`
                  },
                  {
                    status: 404,
                    description: "Not Found - Encounter not found or access denied",
                    example: `{
  "error": "Encounter not found or access denied"
}`
                  },
                  {
                    status: 500,
                    description: "Internal Server Error",
                    example: `{
  "error": "Internal server error"
}`
                  }
                ]}
                example={`curl --request POST \\
  --url 'https://api.revedy.io/v2/encounters/ENC123/notes/edit' \\
  --header 'Authorization: Bearer YOUR_ACCESS_TOKEN' \\
  --header 'Content-Type: application/json' \\
  --data '{
    "note_text": "Patient presented with lower back pain that has persisted for 3 weeks...",
    "description": "Edited follow-up visit note"
  }'`}
              />
              
              <Box p={4} mt={4} borderWidth="1px" borderRadius="lg" borderColor={borderColor}>
                <HStack mb={2}>
                  <InfoIcon color={accentColor} />
                  <Heading as="h4" size="sm">Web-based Note Editor</Heading>
                </HStack>
                <Text mb={2}>
                  For a user-friendly interface to edit encounter notes, you can use our web-based editor:
                </Text>
                <Code p={2} borderRadius="md" bg={codeBg}>
                  https://app.revedy.io/encounter-note-edit?eid=&lt;encounter_id&gt;
                </Code>
                <Text mt={2}>
                  This page allows users to view the current note, make edits, and save changes. 
                  When saved, the note is updated and CPT/ICD10 codes are automatically recoded.
                </Text>
              </Box>
              
              <EndpointSection
                id="save-note"
                title="Save Note"
                method="POST"
                endpoint="/v2/encounters/{encounter_id}/notes"
                description="Saves a new note for a specific encounter."
                headers="Authorization: Bearer YOUR_ACCESS_TOKEN\nContent-Type: application/json"
                pathParams={[
                  {
                    name: "encounter_id",
                    description: "The internal or external identifier for the encounter",
                    required: true
                  }
                ]}
                requestBody={`{
  "note_text": "Note text...",
  "description": "Note description",
  "prompt": "Optional prompt used for generation",
  "model_parameters": "Optional model parameters",
  "icd10_codes": ["Optional", "ICD10", "Codes"]
}`}
                responses={[
                  {
                    status: 201,
                    description: "Created - Note saved successfully",
                    example: `{
  "message": "Note saved successfully"
}`
                  },
                  {
                    status: 400,
                    description: "Bad Request - Missing required note text or description",
                    example: `{
  "error": "Missing required note text or description"
}`
                  },
                  {
                    status: 404,
                    description: "Not Found - Encounter not found or access denied",
                    example: `{
  "error": "Encounter not found or access denied"
}`
                  },
                  {
                    status: 500,
                    description: "Internal Server Error",
                    example: `{
  "error": "Failed to save note"
}`
                  }
                ]}
                example={`curl --request POST \\
  --url 'https://api.revedy.io/v2/encounters/ENC123/notes' \\
  --header 'Authorization: Bearer YOUR_ACCESS_TOKEN' \\
  --header 'Content-Type: application/json' \\
  --data '{
    "note_text": "Patient presented with symptoms of acute sinusitis...",
    "description": "Initial evaluation"
  }'`}
              />
              
              <EndpointSection
                id="get-recent-note"
                title="Get Recent Note"
                method="GET"
                endpoint="/v2/encounters/{encounter_id}/notes/recent"
                description="Retrieves the most recent note for a specific encounter."
                headers="Authorization: Bearer YOUR_ACCESS_TOKEN"
                pathParams={[
                  {
                    name: "encounter_id",
                    description: "The internal or external identifier for the encounter",
                    required: true
                  }
                ]}
                responses={[
                  {
                    status: 200,
                    description: "Successful response",
                    example: `{
  "recent_note": {
    "id": 2829,
    "note": "Note text...",
    "note_created_at": "Fri, 31 May 2024 18:43:01 GMT",
    "note_id": 2017,
    "transcription_raw_text": "transcription.",
    "transcription_structured_text": "\\nDoctor: Okay.",
    "version_id": 1
  }
}`
                  },
                  {
                    status: 404,
                    description: "Not Found - Encounter not found, access denied, or no notes found",
                    example: `{
  "error": "No notes found for this encounter"
}`
                  },
                  {
                    status: 500,
                    description: "Internal Server Error",
                    example: `{
  "error": "Failed to retrieve note"
}`
                  }
                ]}
                example={`curl --request GET \\
  --url 'https://api.revedy.io/v2/encounters/ENC123/notes/recent' \\
  --header 'Authorization: Bearer YOUR_ACCESS_TOKEN'`}
              />
            </Box>

            {/* Patient Content Section */}
            <Box mb={10}>
              <Heading as="h2" size="lg" mb={6}>Patient Content</Heading>
              
              <EndpointSection
                id="generate-patient-content"
                title="Generate Patient Facing Content"
                method="POST"
                endpoint="/v2/encounters/{encounter_id}/patient-facing-content"
                description="Generates patient-facing content for a specific encounter."
                headers="Authorization: Bearer YOUR_ACCESS_TOKEN\nContent-Type: application/json"
                pathParams={[
                  {
                    name: "encounter_id",
                    description: "The internal or external identifier for the encounter",
                    required: true
                  }
                ]}
                requestBody={`{
  "reading_level": "5th grade",
  "user_provided_content": "User provided content (optional)"
}`}
                responses={[
                  {
                    status: 200,
                    description: "Successful response",
                    example: `{
  "patient_facing_content": "Your doctor found that you have a pinched nerve in your neck. This is called cervical radiculopathy. It happens when a nerve in your neck gets squeezed or irritated. This can cause pain, numbness, or weakness in your neck, shoulder, arm, or hand..."
}`
                  },
                  {
                    status: 400,
                    description: "Bad Request - Invalid reading level provided",
                    example: `{
  "error": "Invalid reading level provided"
}`
                  },
                  {
                    status: 404,
                    description: "Not Found - Encounter not found or access denied",
                    example: `{
  "error": "Encounter not found or access denied"
}`
                  },
                  {
                    status: 500,
                    description: "Internal Server Error",
                    example: `{
  "error": "internal error"
}`
                  }
                ]}
                example={`curl --request POST \\
  --url 'https://api.revedy.io/v2/encounters/ENC123/patient-facing-content' \\
  --header 'Authorization: Bearer YOUR_ACCESS_TOKEN' \\
  --header 'Content-Type: application/json' \\
  --data '{
    "reading_level": "5th grade"
  }'`}
              />
              
              <Box p={4} mt={4} borderWidth="1px" borderRadius="lg" borderColor={borderColor}>
                <HStack mb={2}>
                  <InfoIcon color={accentColor} />
                  <Heading as="h4" size="sm">Reading Levels</Heading>
                </HStack>
                <Text mb={2}>
                  The valid reading levels for patient-facing content are:
                </Text>
                <UnorderedList pl={6}>
                  <ListItem>"2nd grade" - Simple language for very basic comprehension</ListItem>
                  <ListItem>"5th grade" - Moderate complexity, suitable for most patients</ListItem>
                  <ListItem>"10th grade" - More advanced language for patients with higher literacy</ListItem>
                </UnorderedList>
              </Box>
              
              <EndpointSection
                id="get-recent-patient-content"
                title="Get Recent Patient Facing Content"
                method="GET"
                endpoint="/v2/encounters/{encounter_id}/patient-facing-content/recent"
                description="Retrieves the most recent patient-facing content for a specific encounter."
                headers="Authorization: Bearer YOUR_ACCESS_TOKEN"
                pathParams={[
                  {
                    name: "encounter_id",
                    description: "The internal or external identifier for the encounter",
                    required: true
                  }
                ]}
                responses={[
                  {
                    status: 200,
                    description: "Successful response",
                    example: `{
  "recent_content": {
    "content": "Your doctor found that you have a pinched nerve in your neck...",
    "create_date": "Fri, 31 May 2024 18:58:54 GMT",
    "encounter_id": "d1f64855-5c34-4a4a-b676-20e64dec9c38",
    "id": 10,
    "reading_level": "2nd grade",
    "status": "generated"
  }
}`
                  },
                  {
                    status: 404,
                    description: "Not Found - Encounter not found or access denied, or no patient-facing content found",
                    example: `{
  "error": "No patient-facing content found for this encounter"
}`
                  },
                  {
                    status: 500,
                    description: "Internal Server Error",
                    example: `{
  "error": "Failed to retrieve recent patient-facing content"
}`
                  }
                ]}
                example={`curl --request GET \\
  --url 'https://api.revedy.io/v2/encounters/ENC123/patient-facing-content/recent' \\
  --header 'Authorization: Bearer YOUR_ACCESS_TOKEN'`}
              />
              
              <EndpointSection
                id="get-all-patient-content"
                title="Get All Patient Facing Content"
                method="GET"
                endpoint="/v2/encounters/{encounter_id}/patient-facing-content/all"
                description="Retrieves all patient-facing content for a specific encounter."
                headers="Authorization: Bearer YOUR_ACCESS_TOKEN"
                pathParams={[
                  {
                    name: "encounter_id",
                    description: "The internal or external identifier for the encounter",
                    required: true
                  }
                ]}
                responses={[
                  {
                    status: 200,
                    description: "Successful response",
                    example: `{
  "all_content": [
    {
      "content": "Your doctor found that you have a pinched nerve in your neck...",
      "create_date": "Fri, 31 May 2024 18:58:54 GMT",
      "encounter_id": "d1f64855-5c34-4a4a-b676-20e64dec9c38",
      "id": 10,
      "reading_level": "2nd grade",
      "status": "generated"
    },
    {
      "content": "During your visit, your doctor diagnosed you with cervical radiculopathy...",
      "create_date": "Fri, 31 May 2024 18:59:54 GMT",
      "encounter_id": "d1f64855-5c34-4a4a-b676-20e64dec9c38",
      "id": 11,
      "reading_level": "5th grade",
      "status": "user_provided"
    }
  ]
}`
                  },
                  {
                    status: 404,
                    description: "Not Found - Encounter not found or access denied, or no patient-facing content found",
                    example: `{
  "error": "No patient-facing content found for this encounter"
}`
                  },
                  {
                    status: 500,
                    description: "Internal Server Error",
                    example: `{
  "error": "Failed to retrieve all patient-facing content"
}`
                  }
                ]}
                example={`curl --request GET \\
  --url 'https://api.revedy.io/v2/encounters/ENC123/patient-facing-content/all' \\
  --header 'Authorization: Bearer YOUR_ACCESS_TOKEN'`}
              />
            </Box>

{/* Case Management Section */}
<Box mb={10}>
  <Heading as="h2" size="lg" mb={6}>Case Management</Heading>
  
  <EndpointSection
    id="create-external-case"
    title="Create External Case"
    method="POST"
    endpoint="/create_external_case"
    description="Creates a new external case for coding."
    headers="Authorization: Bearer YOUR_ACCESS_TOKEN\nContent-Type: application/json"
    requestBody={`{
  "external_case_id": "CASE123",
  "case_type": "Surgery"  // Optional: Case type (e.g., "Surgery", "IONM", "E/M")
}`}
    responses={[
      {
        status: 201,
        description: "Created - Case created successfully",
        example: `{
  "message": "External case created successfully",
  "case_id": "b1a4ce7f-a9e9-4f13-8df0-66a221d57b57"
}`
      },
      {
        status: 400,
        description: "Bad Request - Missing required parameters",
        example: `{
  "error": "External case ID required"
}`
      },
      {
        status: 500,
        description: "Internal Server Error",
        example: `{
  "error": "An error occurred while creating the external case"
}`
      }
    ]}
    example={`curl --request POST \\
  --url 'https://api.revedy.io/create_external_case' \\
  --header 'Authorization: Bearer YOUR_ACCESS_TOKEN' \\
  --header 'Content-Type: application/json' \\
  --data '{
    "external_case_id": "CASE123",
    "case_type": "Surgery"
  }'`}
  />
  
  <EndpointSection
    id="list-external-cases"
    title="List External Cases"
    method="GET"
    endpoint="/list_external_cases"
    description="Retrieves a list of external cases with pagination support."
    headers="Authorization: Bearer YOUR_ACCESS_TOKEN"
    queryParams={[
      {
        name: "start",
        description: "Starting index for pagination",
        required: false
      },
      {
        name: "limit",
        description: "Maximum number of cases to return",
        required: false
      }
    ]}
    responses={[
      {
        status: 200,
        description: "Successful response",
        example: `{
  "cases": [
    {
      "id": "b1a4ce7f-a9e9-4f13-8df0-66a221d57b57",
      "external_case_id": "CASE123",
      "date_created": "2023-06-01T14:30:00Z",
      "date_updated": "2023-06-01T15:45:00Z",
      "case_description": "Appendectomy procedure",
      "encounter_id": "ENC456",
      "case_type": "Surgery",
      "status": "active",
      "cpt_status": "complete",
      "icd10_status": "not_started",
      "ionm_case_id": null,
      "files": {
        "5": {
          "file_name": "clinical_notes.txt",
          "file_id": 123
        }
      }
    }
  ],
  "pagination": {
    "total": 50,
    "start": 0,
    "limit": 20,
    "has_more": true
  }
}`
      },
      {
        status: 500,
        description: "Internal Server Error",
        example: `{
  "error": "An error occurred while listing external cases"
}`
      }
    ]}
    example={`curl --request GET \\
  --url 'https://api.revedy.io/list_external_cases?start=0&limit=20' \\
  --header 'Authorization: Bearer YOUR_ACCESS_TOKEN'`}
  />
  
  <EndpointSection
    id="get-case-details"
    title="Get Case Details"
    method="GET"
    endpoint="/get_case_details/{case_id}"
    description="Retrieves detailed information about a specific case, including files and coding results."
    headers="Authorization: Bearer YOUR_ACCESS_TOKEN"
    pathParams={[
      {
        name: "case_id",
        description: "The ID of the case to retrieve",
        required: true
      }
    ]}
    responses={[
      {
        status: 200,
        description: "Successful response",
        example: `{
  "id": "b1a4ce7f-a9e9-4f13-8df0-66a221d57b57",
  "external_case_id": "CASE123",
  "case_type": "Surgery",
  "date_created": "2023-06-01T14:30:00Z",
  "date_updated": "2023-06-01T15:45:00Z",
  "status": "active",
  "cpt_status": "complete",
  "icd10_status": "not_started",
  "cpt_coding_results": {
    "99213": {
      "billable": true,
      "cpt_code": "99213",
      "decision": "E/M code 99213 selected",
      "detail": "Overall MDM complexity: low",
      "justification": "E/M code 99213 selected based on...",
      "long_description": "Office or other outpatient visit...",
      "modifiers": "",
      "units": 1,
      "work_rvu": 0.97
    }
  },
  "icd10_coding_results": null,
  "files": [
    {
      "id": 123,
      "file_name": "clinical_notes.txt",
      "file_type": "Clinical Notes",
      "file_size": 2048,
      "file_text": "Patient presented with...",
      "upload_date": "2023-06-01T14:35:00Z"
    }
  ],
  "ionm_case_id": null,
  "encounter_id": "ENC456",
  "coder_notes": "This case involves a standard appendectomy procedure...",
  "coder_score": 92
}`
      },
      {
        status: 404,
        description: "Not Found - Case not found or access denied",
        example: `{
  "error": "Case not found or access denied"
}`
      },
      {
        status: 500,
        description: "Internal Server Error",
        example: `{
  "error": "An error occurred while getting case details"
}`
      }
    ]}
    example={`curl --request GET \\
  --url 'https://api.revedy.io/get_case_details/b1a4ce7f-a9e9-4f13-8df0-66a221d57b57' \\
  --header 'Authorization: Bearer YOUR_ACCESS_TOKEN'`}
  />
</Box>

{/* Content Management Section */}
<Box mb={10}>
  <Heading as="h2" size="lg" mb={6}>Content Management</Heading>

  <EndpointSection
    id="simplified-upload-file"
    title="Simplified File Upload (Find or Create Case)"
    method="POST"
    endpoint="/v2/files/upload"
    description="Uploads a file and associates it with a case. If the case does not exist, it will be created automatically. This is the recommended endpoint for all new file uploads."
    headers="Authorization: Bearer YOUR_ACCESS_TOKEN\nContent-Type: multipart/form-data"
    requestBody={`external_case_id: Your unique identifier for the case (string, required).
file: The file to upload (file part, required).
file_type_id: The type ID of the file (e.g., 5 for Clinical Notes, optional, defaults to 5).
document_category: A category for the document (string, optional).
claim_id: An associated claim ID (string, optional).`}
    responses={[
      {
        status: 201,
        description: "Created - File uploaded successfully to a new or existing case.",
        example: `{
  "message": "File uploaded successfully",
  "file_id": 456
}`
      },
      {
        status: 200,
        description: "OK - A duplicate of this file already exists for this case. No new file was stored.",
        example: `{
  "message": "File already exists",
  "file_id": 123
}`
      },
      {
        status: 400,
        description: "Bad Request - Missing required form fields like 'external_case_id' or 'file'.",
        example: `{
  "error": "external_case_id form field is required"
}`
      }
    ]}
    example={`curl --request POST \\
  --url 'https://api.revedy.io/v2/files/upload' \\
  --header 'Authorization: Bearer YOUR_ACCESS_TOKEN' \\
  --header 'Content-Type: multipart/form-data' \\
  --form 'external_case_id=YOUR_UNIQUE_CASE_ID' \\
  --form 'file=@path/to/your/file.pdf'`}
  />
  
  <EndpointSection
    id="upload-content"
    title="Upload Content to Case"
    method="POST"
    endpoint="/upload_content/{case_id}"
    description="Uploads text content to an existing case."
    headers="Authorization: Bearer YOUR_ACCESS_TOKEN\nContent-Type: multipart/form-data"
    pathParams={[
      {
        name: "case_id",
        description: "The ID of the case to upload content to",
        required: true
      }
    ]}
    requestBody={`file: The text file to upload (required)
file_type_id: The type ID of the file (e.g., 5 for Clinical Notes)`}
    responses={[
      {
        status: 201,
        description: "Created - Content uploaded successfully",
        example: `{
  "message": "Content uploaded successfully",
  "file_id": 123
}`
      },
      {
        status: 400,
        description: "Bad Request - No file part in the request",
        example: `{
  "error": "No file part in the request"
}`
      },
      {
        status: 404,
        description: "Not Found - Case not found or access denied",
        example: `{
  "error": "Case not found or access denied"
}`
      },
      {
        status: 500,
        description: "Internal Server Error",
        example: `{
  "error": "An error occurred while uploading the content"
}`
      }
    ]}
    example={`curl --request POST \\
  --url 'https://api.revedy.io/upload_content/b1a4ce7f-a9e9-4f13-8df0-66a221d57b57' \\
  --header 'Authorization: Bearer YOUR_ACCESS_TOKEN' \\
  --header 'Content-Type: multipart/form-data' \\
  --form 'file=@content.txt' \\
  --form 'file_type_id=5'`}
  />
  
  <EndpointSection
    id="upload-file"
    title="Upload File to Case"
    method="POST"
    endpoint="/upload_file/{case_id}"
    description="Uploads a file (PDF, image, text, etc.) to an existing case."
    headers="Authorization: Bearer YOUR_ACCESS_TOKEN\nContent-Type: multipart/form-data"
    pathParams={[
      {
        name: "case_id",
        description: "The ID of the case to upload the file to",
        required: true
      }
    ]}
    requestBody={`file: The file to upload (required)
file_type_id: The type ID of the file (e.g., 5 for Clinical Notes)`}
    responses={[
      {
        status: 201,
        description: "Created - File uploaded successfully",
        example: `{
  "message": "File uploaded successfully",
  "file_id": 123
}`
      },
      {
        status: 400,
        description: "Bad Request - No file part in the request or no file selected",
        example: `{
  "error": "No file part in the request"
}`
      },
      {
        status: 404,
        description: "Not Found - Case not found or access denied",
        example: `{
  "error": "Case not found or access denied"
}`
      },
      {
        status: 500,
        description: "Internal Server Error",
        example: `{
  "error": "An error occurred while uploading the file"
}`
      }
    ]}
    example={`curl --request POST \\
  --url 'https://api.revedy.io/upload_file/b1a4ce7f-a9e9-4f13-8df0-66a221d57b57' \\
  --header 'Authorization: Bearer YOUR_ACCESS_TOKEN' \\
  --header 'Content-Type: multipart/form-data' \\
  --form 'file=@operative_report.pdf' \\
  --form 'file_type_id=5'`}
  />
  
  <EndpointSection
    id="download-case-file"
    title="Download Case File"
    method="POST"
    endpoint="/download_case_file"
    description="Downloads a file associated with a case."
    headers="Authorization: Bearer YOUR_ACCESS_TOKEN\nContent-Type: application/json"
    requestBody={`{
  "case_id": "b1a4ce7f-a9e9-4f13-8df0-66a221d57b57",
  "file_id": 123
}`}
    responses={[
      {
        status: 200,
        description: "Successful response - Returns the file content",
        example: "Binary file content with appropriate Content-Type header"
      },
      {
        status: 404,
        description: "Not Found - Case or file not found",
        example: `{
  "error": "File not found"
}`
      },
      {
        status: 403,
        description: "Forbidden - Access denied",
        example: `{
  "error": "Case not found or access denied"
}`
      },
      {
        status: 500,
        description: "Internal Server Error",
        example: `{
  "error": "An error occurred while downloading the file"
}`
      }
    ]}
    example={`curl --request POST \\
  --url 'https://api.revedy.io/download_case_file' \\
  --header 'Authorization: Bearer YOUR_ACCESS_TOKEN' \\
  --header 'Content-Type: application/json' \\
  --data '{
    "case_id": "b1a4ce7f-a9e9-4f13-8df0-66a221d57b57",
    "file_id": 123
  }' \\
  --output downloaded_file.pdf`}
  />
</Box>

{/* Coding Services Section */}
<Box mb={10}>
  <Heading as="h2" size="lg" mb={6}>Coding Services</Heading>
  
  <EndpointSection
    id="cpt-coding"
    title="CPT Coding"
    method="POST"
    endpoint="/cpt_coding"
    description="Processes a case to generate CPT codes based on the case content."
    headers="Authorization: Bearer YOUR_ACCESS_TOKEN\nContent-Type: application/json"
    requestBody={`{
  "case_id": "b1a4ce7f-a9e9-4f13-8df0-66a221d57b57",
  "case_type": "Surgery"  // Optional: Overrides the case type stored in the database
}`}
    responses={[
      {
        status: 200,
        description: "OK - Processing completed successfully",
        example: `{
  "status": "success",
  "results": {
    "99213": {
      "billable": true,
      "cpt_code": "99213",
      "decision": "E/M code 99213 selected",
      "detail": "Overall MDM complexity: low",
      "justification": "E/M code 99213 selected based on...",
      "long_description": "Office or other outpatient visit...",
      "modifiers": "",
      "units": 1,
      "work_rvu": 0.97
    }
  }
}`
      },
      {
        status: 400,
        description: "Bad Request - Missing required parameters",
        example: `{
  "error": "Missing required case_id or encounter_id parameter"
}`
      },
      {
        status: 500,
        description: "Internal Server Error",
        example: `{
  "status": "error",
  "message": "An error occurred: Failed to process case"
}`
      }
    ]}
    example={`curl --request POST \\
  --url 'https://api.revedy.io/cpt_coding' \\
  --header 'Authorization: Bearer YOUR_ACCESS_TOKEN' \\
  --header 'Content-Type: application/json' \\
  --data '{
    "case_id": "b1a4ce7f-a9e9-4f13-8df0-66a221d57b57",
    "case_type": "Surgery"
  }'`}
  />
  
  <EndpointSection
    id="icd10-coding"
    title="ICD-10 Coding"
    method="POST"
    endpoint="/icd10_coding"
    description="Processes a case to generate ICD-10 diagnosis codes based on the case content."
    headers="Authorization: Bearer YOUR_ACCESS_TOKEN\nContent-Type: application/json"
    requestBody={`{
  "case_id": "b1a4ce7f-a9e9-4f13-8df0-66a221d57b57"
}`}
    responses={[
      {
        status: 200,
        description: "OK - Processing completed successfully",
        example: `{
  "status": "success",
  "results": {
    "M54.5": {
      "clean_code": "M545",
      "code_type": "diagnosis",
      "condition": "Low back pain",
      "confidence": 90,
      "description": "Low back pain",
      "evidence": "Patient reports chronic lower back pain...",
      "icd10_code": "M54.5",
      "is_primary": true,
      "justification": "The documentation clearly indicates the patient has low back pain...",
      "status": "current"
    },
    "I10": {
      "clean_code": "I10",
      "code_type": "diagnosis",
      "condition": "Essential hypertension",
      "confidence": 85,
      "description": "Essential (primary) hypertension",
      "evidence": "Patient has documented history of hypertension...",
      "icd10_code": "I10",
      "is_primary": false,
      "justification": "The documentation indicates the patient has a history of hypertension...",
      "status": "current"
    }
  }
}`
      },
      {
        status: 400,
        description: "Bad Request - Missing required parameters",
        example: `{
  "error": "Missing required case_id or encounter_id parameter"
}`
      },
      {
        status: 500,
        description: "Internal Server Error",
        example: `{
  "status": "error",
  "message": "An error occurred: Failed to process case"
}`
      }
    ]}
    example={`curl --request POST \\
  --url 'https://api.revedy.io/icd10_coding' \\
  --header 'Authorization: Bearer YOUR_ACCESS_TOKEN' \\
  --header 'Content-Type: application/json' \\
  --data '{
    "case_id": "b1a4ce7f-a9e9-4f13-8df0-66a221d57b57"
  }'`}
  />
  
  <EndpointSection
    id="generate-case-overview"
    title="Generate Case Overview"
    method="POST"
    endpoint="/generate_case_overview"
    description="Generates a summary overview of a case based on its content."
    headers="Authorization: Bearer YOUR_ACCESS_TOKEN\nContent-Type: application/json"
    requestBody={`{
  "encounter_id": "ENC456"
}`}
    responses={[
      {
        status: 200,
        description: "Successful response",
        example: `{
  "case_overview": "This case involves a 45-year-old male patient who presented with acute appendicitis. The patient underwent a laparoscopic appendectomy procedure on June 1, 2023. The procedure was performed without complications, and the patient was discharged the same day. The primary diagnosis is acute appendicitis (K35.80)."
}`
      },
      {
        status: 400,
        description: "Bad Request - Missing required parameters",
        example: `{
  "error": "Missing required encounter_id parameter"
}`
      },
      {
        status: 404,
        description: "Not Found - Case not found or access denied",
        example: `{
  "error": "Case not found or user does not have permission"
}`
      },
      {
        status: 500,
        description: "Internal Server Error",
        example: `{
  "error": "An unexpected error occurred"
}`
      }
    ]}
    example={`curl --request POST \\
  --url 'https://api.revedy.io/generate_case_overview' \\
  --header 'Authorization: Bearer YOUR_ACCESS_TOKEN' \\
  --header 'Content-Type: application/json' \\
  --data '{
    "encounter_id": "ENC456"
  }'`}
  />
</Box>


{/* Medical Necessity Analysis Section */}
<Box mb={10}>
  <Heading as="h2" size="lg" mb={6}>Medical Necessity Analysis</Heading>
  
  <EndpointSection
    id="medical-necessity-analysis"
    title="Analyze Medical Necessity"
    method="POST"
    endpoint="/medical_necessity_analysis"
    description="Evaluates the medical necessity of CPT codes based on patient case data and payer requirements."
    headers="Authorization: Bearer YOUR_ACCESS_TOKEN\nContent-Type: application/json"
    requestBody={`{
  "case_id": "12345",
  "external_case_id": "EXT-12345",  // Optional
  "cpt_code": "99204",  // Optional - if omitted, all CPT codes for the case will be analyzed
  "payer": "cms",  // Optional - "cms" (Medicare) or "aetna", default is "cms"
  "override_state": true,  // Optional - when true, ignores hospital state and uses contractor with best coverage
  "coding_context": "",  // Optional - additional context for coding analysis
  "case_type": "",  // Optional - type of case (e.g., "IONM")
  "summary": true  // Optional - whether to include a summary in the response, default is true
}`}
    responses={[
      {
        status: 200,
        description: "Successful response",
        example: `{
  "status": "success",
  "results": [
    {
      "cpt_code": "99204",
      "analysis": {
        "explanation": "CPT 99204 is an office or other outpatient visit for the evaluation and management of a new patient that requires...",
        "icd10_review": "- **M54.12: Radiculopathy, cervical region** - This should be the primary diagnosis as the physician's assessment...",
        "key_factors": "#### Positive Factors:\\n- New patient status clearly established\\n- Comprehensive history documented\\n...",
        "recommendations": "- Include documentation of vital signs in future encounters\\n- Document a more comprehensive review of systems\\n...",
        "score": 100,
        "used_sources": [
          {
            "source_id": "LCD36593",
            "source_type": "lcd",
            "contractor": {
              "contractor_bus_name": "Palmetto GBA",
              "contractor_id": "374",
              "states_covered": ["48"]
              // Additional contractor details
            },
            "reason": "no state provided, best coverage",
            "relevancy_explanation": "The provided text details the coverage criteria, indications, limitations, and requirements for polysomnography..."
          }
        ],
        "unused_sources": [
          // Similar structure to used_sources
        ]
      }
    }
  ],
  "cpt_codes_data": {
    "99204": {
      "billable": true,
      "cpt_code": "99204",
      "decision": "E/M code 99204 selected",
      "detail": "Overall MDM complexity: moderate",
      "justification": "E/M code 99204 selected based on:\\n- Problem complexity: moderate\\n- Data complexity: low\\n- Risk level: moderate\\n- Patient type: new",
      "long_description": "Office or other outpatient visit for the evaluation and management of a new patient...",
      "modifiers": "",
      "units": 1,
      "work_rvu": 2.6
      // Additional CPT code details
    }
  },
  "summary_data": {
    "analysis": "The documentation for this new patient visit (CPT 99204) demonstrates moderate complexity medical decision making...",
    "recommendations": "1. **Add comprehensive vital signs documentation**\\n   VITAL SIGNS:\\n   Temperature: 98.6°F\\n...",
    "idtf": "The documentation does not specifically address Independent Diagnostic Testing Facility (IDTF) requirements..."
  }
}`
      },
      {
        status: 400,
        description: "Bad Request - Missing required parameters",
        example: `{
  "error": "Missing required case_id parameter"
}`
      },
      {
        status: 500,
        description: "Internal Server Error",
        example: `{
  "status": "error",
  "message": "An error occurred during analysis"
}`
      }
    ]}
    example={`curl --request POST \\
  --url 'https://api.revedy.io/medical_necessity_analysis' \\
  --header 'Authorization: Bearer YOUR_ACCESS_TOKEN' \\
  --header 'Content-Type: application/json' \\
  --data '{
    "case_id": "12345",
    "payer": "cms",
    "override_state": true
  }'`}
  />
  
  <EndpointSection
    id="mn-generate-pdf"
    title="Generate Medical Necessity PDF"
    method="POST"
    endpoint="/mn_generate_pdf"
    description="Generates a PDF report of the medical necessity analysis."
    headers="Authorization: Bearer YOUR_ACCESS_TOKEN\nContent-Type: application/json"
    requestBody={`{
  "cptCode": "99204",
  "analysis": {
    "explanation": "CPT 99204 is an office or other outpatient visit...",
    "key_factors": "#### Positive Factors:\\n- New patient status clearly established\\n...",
    "recommendations": "- Include documentation of vital signs in future encounters\\n...",
    "score": 100
  },
  "usedSources": [
    {
      "source_id": "LCD36593",
      "source_type": "lcd",
      "contractor": {
        "contractor_bus_name": "Palmetto GBA"
        // Additional contractor details
      },
      "reason": "no state provided, best coverage",
      "relevancy_explanation": "The provided text details the coverage criteria..."
    }
  ],
  "unusedSources": [
    // Similar structure to usedSources
  ],
  "payer": "cms",
  "score": 100
}`}
    responses={[
      {
        status: 200,
        description: "Successful response - Returns a PDF file",
        example: "Binary PDF data"
      },
      {
        status: 500,
        description: "Internal Server Error",
        example: `{
  "error": "Error generating PDF"
}`
      }
    ]}
    example={`curl --request POST \\
  --url 'https://api.revedy.io/mn_generate_pdf' \\
  --header 'Authorization: Bearer YOUR_ACCESS_TOKEN' \\
  --header 'Content-Type: application/json' \\
  --data '{
    "cptCode": "99204",
    "analysis": {
      "explanation": "CPT 99204 is an office or other outpatient visit...",
      "key_factors": "#### Positive Factors:\\n- New patient status clearly established\\n...",
      "recommendations": "- Include documentation of vital signs in future encounters\\n...",
      "score": 100
    },
    "usedSources": [...],
    "unusedSources": [...],
    "payer": "cms",
    "score": 100
  }' \\
  --output medical_necessity_report.pdf`}
  />
  
  <EndpointSection
    id="submit-medical-necessity-feedback"
    title="Submit Medical Necessity Feedback"
    method="POST"
    endpoint="/submit_medical_necessity_feedback"
    description="Submits user feedback on a medical necessity analysis."
    headers="Authorization: Bearer YOUR_ACCESS_TOKEN\nContent-Type: application/json"
    requestBody={`{
  "case_id": "12345",
  "encounter_id": "12345",
  "cpt_code": "99204",
  "feedback_text": "This analysis was helpful but missed consideration of...",
  "payer": "cms"
}`}
    responses={[
      {
        status: 200,
        description: "Successful response",
        example: `{
  "status": "success",
  "message": "Medical necessity feedback submitted successfully"
}`
      },
      {
        status: 500,
        description: "Internal Server Error",
        example: `{
  "error": "Error submitting feedback"
}`
      }
    ]}
    example={`curl --request POST \\
  --url 'https://api.revedy.io/submit_medical_necessity_feedback' \\
  --header 'Authorization: Bearer YOUR_ACCESS_TOKEN' \\
  --header 'Content-Type: application/json' \\
  --data '{
    "case_id": "12345",
    "encounter_id": "12345",
    "cpt_code": "99204",
    "feedback_text": "This analysis was helpful but missed consideration of...",
    "payer": "cms"
  }'`}
  />
  
  <EndpointSection
    id="get-medical-necessity-feedback"
    title="Get Medical Necessity Feedback"
    method="GET"
    endpoint="/get_medical_necessity_feedback/{encounter_id}"
    description="Retrieves all feedback submitted for a specific encounter's medical necessity analysis."
    headers="Authorization: Bearer YOUR_ACCESS_TOKEN"
    pathParams={[
      {
        name: "encounter_id",
        description: "The ID of the encounter to retrieve feedback for",
        required: true
      }
    ]}
    queryParams={[
      {
        name: "payer",
        description: "Filter feedback by payer (e.g., 'cms', 'aetna')",
        required: false
      }
    ]}
    responses={[
      {
        status: 200,
        description: "Successful response",
        example: `{
  "status": "success",
  "feedback": [
    {
      "id": 1,
      "case_id": "12345",
      "cpt_code": "99204",
      "user_id": 42,
      "feedback_text": "This analysis was helpful but missed consideration of...",
      "payer": "cms",
      "created_at": "2023-06-15T14:30:00.000Z"
    },
    {
      "id": 2,
      "case_id": "12345",
      "cpt_code": "99214",
      "user_id": 42,
      "feedback_text": "The analysis correctly identified all key factors...",
      "payer": "cms",
      "created_at": "2023-06-16T10:15:00.000Z"
    }
  ]
}`
      },
      {
        status: 500,
        description: "Internal Server Error",
        example: `{
  "error": "Error retrieving feedback"
}`
      }
    ]}
    example={`curl --request GET \\
  --url 'https://api.revedy.io/get_medical_necessity_feedback/12345?payer=cms' \\
  --header 'Authorization: Bearer YOUR_ACCESS_TOKEN'`}
  />

  <Box p={4} mt={4} borderWidth="1px" borderRadius="lg" borderColor={borderColor}>
    <HStack mb={2}>
      <InfoIcon color={accentColor} />
      <Heading as="h4" size="sm">Try Medical Necessity Analysis</Heading>
    </HStack>
    <Text mb={2}>
      You can try the Medical Necessity Analysis tool through our web interface:
    </Text>
    <Link href="/medical-necessity-analysis" isExternal color="blue.500">
      Medical Necessity Analysis Tool <ExternalLinkIcon mx="2px" />
    </Link>
    <Text mt={2}>
      This interface allows you to analyze the medical necessity of CPT codes for specific cases,
      view detailed analysis results, and generate PDF reports.
    </Text>
  </Box>
</Box>


            {/* Error Codes Section */}
            <Box id="error-codes" mb={10}>
              <Heading as="h2" size="lg" mb={4}>Error Codes</Heading>
              <Text mb={4}>
                The API uses standard HTTP status codes to indicate the success or failure of requests.
                Here are the common error codes you may encounter:
              </Text>
              
              <Table size="sm" variant="simple">
                <Thead>
                  <Tr>
                    <Th>Status Code</Th>
                    <Th>Description</Th>
                  </Tr>
                </Thead>
                <Tbody>
                  <Tr>
                    <Td>200 OK</Td>
                    <Td>The request was successful</Td>
                  </Tr>
                  <Tr>
                    <Td>201 Created</Td>
                    <Td>The resource was successfully created</Td>
                  </Tr>
                  <Tr>
                    <Td>400 Bad Request</Td>
                    <Td>The request was invalid or cannot be served</Td>
                  </Tr>
                  <Tr>
                    <Td>401 Unauthorized</Td>
                    <Td>Authentication is required and has failed or has not been provided</Td>
                  </Tr>
                  <Tr>
                    <Td>403 Forbidden</Td>
                    <Td>The server understood the request but refuses to authorize it</Td>
                  </Tr>
                  <Tr>
                    <Td>404 Not Found</Td>
                    <Td>The requested resource could not be found</Td>
                  </Tr>
                  <Tr>
                    <Td>429 Too Many Requests</Td>
                    <Td>Too many requests have been sent in a given amount of time</Td>
                  </Tr>
                  <Tr>
                    <Td>500 Internal Server Error</Td>
                    <Td>An unexpected condition was encountered on the server</Td>
                  </Tr>
                </Tbody>
              </Table>
              
              <Heading as="h3" size="md" mt={6} mb={3}>Specific Error Codes</Heading>
              <Table size="sm" variant="simple">
                <Thead>
                  <Tr>
                    <Th>Error Code</Th>
                    <Th>Description</Th>
                  </Tr>
                </Thead>
                <Tbody>
                  <Tr>
                    <Td><Code>no_file_part</Code></Td>
                    <Td>No file was included in the request</Td>
                  </Tr>
                  <Tr>
                    <Td><Code>error_generating_event_id</Code></Td>
                    <Td>There was an error generating the event ID</Td>
                  </Tr>
                  <Tr>
                    <Td><Code>transcription_failed</Code></Td>
                    <Td>The transcription of the audio file failed</Td>
                  </Tr>
                </Tbody>
              </Table>
            </Box>

            {/* Rate Limits Section */}
            <Box id="rate-limits" mb={10}>
              <Heading as="h2" size="lg" mb={4}>Rate Limits</Heading>
              <Text mb={4}>
                The API limits the number of requests that can be made to prevent abuse. If you exceed these limits, 
                you will receive a <Code>429 Too Many Requests</Code> response.
              </Text>
              
              <Table size="sm" variant="simple">
                <Thead>
                  <Tr>
                    <Th>Endpoint</Th>
                    <Th>Rate Limit</Th>
                  </Tr>
                </Thead>
                <Tbody>
                  <Tr>
                    <Td>Authentication endpoints</Td>
                    <Td>10 requests per minute</Td>
                  </Tr>
                  <Tr>
                    <Td>Encounter analysis endpoints</Td>
                    <Td>30 requests per minute</Td>
                  </Tr>
                  <Tr>
                    <Td>All other endpoints</Td>
                    <Td>60 requests per minute</Td>
                  </Tr>
                </Tbody>
              </Table>
              
              <Text mt={4}>
                If you need higher rate limits for your application, please contact our support team.
              </Text>
            </Box>

            {/* Version Info Section */}
            <Box id="version-info" mb={10}>
              <Heading as="h2" size="lg" mb={4}>Version Information</Heading>
              <Text mb={4}>
                You can check the current API version using the version endpoint:
              </Text>
              
              <EndpointSection
                id="version-endpoint"
                title="Get API Version"
                method="GET"
                endpoint="/version"
                description="Retrieves the current API version."
                headers="Authorization: Bearer YOUR_ACCESS_TOKEN"
                responses={[
                  {
                    status: 200,
                    description: "Successful response",
                    example: `{
  "version": "2.48"
}`
                  },
                  {
                    status: 401,
                    description: "Unauthorized - Invalid or missing token",
                    example: `{
  "error": "Unauthorized access"
}`
                  }
                ]}
                example={`curl --request GET \\
  --url 'https://api.revedy.io/version' \\
  --header 'Authorization: Bearer YOUR_ACCESS_TOKEN'`}
              />
              
              <Button 
                onClick={handleGetVersionClick} 
                isLoading={loadingVersion}
                colorScheme="blue"
                mt={4}
              >
                Get Current Version
              </Button>
              
              {version && (
                <Box p={4} mt={4} borderWidth="1px" borderRadius="md" bg={codeBg}>
                  <Text><strong>Current API Version:</strong> {version}</Text>
                </Box>
              )}
            </Box>
          </Box>
        </GridItem>

        {/* Footer */}
        <GridItem area="footer" bg={bgColor} borderTop="1px" borderColor={borderColor} p={4} textAlign="center">
          <Text fontSize="sm">© {new Date().getFullYear()} Revedy. All rights reserved.</Text>
        </GridItem>
      </Grid>
    </Box>
  );
}
export default APIDocsV2;


